import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp} from '@utils';
import {metaInfo} from '@configs';
import {HOME, SITEMAP} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {useDispatch} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {recruiterService} from '@jobseeker/common-core/services';
import type {GetServerSidePropsContext} from 'next/types';
import CharactersList from '@components/Sitemap/JobCategories/CharactersList';
import {Box, Grid, Typography} from '@mui/material';
import {cleanUrl} from '@jobseeker/lotus/utils';
import {Link} from '@jobseeker/lotus/elements';

type SitemapProps = {
  seoConfig: object;
  metaData: object;
  char: string;
  recruiters: {url: string; name: string}[];
  canonicalUrl: string;
};

const RecruiterTitle = ({
  seoConfig,
  metaData,
  char,
  recruiters,
  canonicalUrl,
}: SitemapProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: 'Sitemap',
      item: `/sitemap`,
    },
    {
      name: `Recruiter - ${char.toUpperCase()}`,
      item: '',
    },
  ];

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout
          seoConfig={{
            ...seoConfig,
            canonical: `${
              (seoConfig as {canonical?: string}).canonical
            }${canonicalUrl.replace('/sitemap', '')}`,
          }}
        />
        <NavLayout>
          <ResponsiveLayout
            breadcrumbElements={breadcrumbElements}
            variant={VersionVariants.VERSION_TWO}
          >
            <Box
              sx={{
                mx: isAppIimjobs ? 0 : 2,
              }}
            >
              <Typography component="h2" mb={2} mt={isAppIimjobs ? 0 : 6}>
                Recruiter - {char.toUpperCase()}
              </Typography>
              <CharactersList text="Recruiter" />
              <Grid container>
                {recruiters.map((recruiter, index) => (
                  <Grid item key={index} xs={4}>
                    <Link href={recruiter.url} target="_blank">
                      {recruiter.name}
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const canonicalUrl = context?.resolvedUrl;
    const {slug = ''} = context.query;
    const char =
      String(slug)
        .replace(/\.html/, '')
        .split('-')
        .pop() ?? 'a';

    const metaData = getConfigByApp(metaInfo, HOME, true, SITEMAP);

    const seoConfig = await loadSEO(SITEMAP);

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: SITEMAP},
    });

    const baseUrl = process.env.NEXT_PUBLIC_LOAD_CONFIG;

    const {
      data: recruiters,
    }: {status: object; data: {id: number; name: string}[]} =
      await recruiterService.getRecruiters('/recruiter-list', {
        name: char,
      });

    const recruitersMetadata: {url: string; name: string}[] = [];

    recruiters?.forEach(({id, name}) => {
      const url = new URL(`/r/${id}-${cleanUrl(name)}`, baseUrl).href;

      recruitersMetadata.push({url, name});
    });

    // Sort the list in alphabetical order
    recruitersMetadata.sort((a, b) => {
      if (a.name.toUpperCase() < b.name.toUpperCase()) {
        return -1;
      }
      return 1;
    });

    return {
      props: {
        seoConfig,
        metaData,
        char,
        recruiters: recruitersMetadata,
        canonicalUrl,
      },
    };
  }
);

export default RecruiterTitle;
